import type { Point } from "../canvas/canvasTypes";
import {
  moveSelectionTransform,
  resizeSelectionTransform,
  selectionPointerAngleDegrees,
  selectionRotationHandlePlacement,
  selectionTransformCorners,
  shortestSignedAngularDeltaDegrees,
  transformsDiffer,
  rotateSelectionTransform,
} from "./selectionGeometry";
import type {
  IntegerRect,
  RasterSelectionAction,
  RasterSelectionHandle,
  RasterSelectionRotationHandleSide,
  RasterSelectionTransform,
} from "./selectionTypes";

export const DEFAULT_RASTER_TRANSFORM_THRESHOLD_CSS_PIXELS = 4;

export interface CompletedRasterSelectionTransform {
  readonly action: Extract<RasterSelectionAction, "move" | "resize" | "rotate">;
  readonly before: RasterSelectionTransform;
  readonly after: RasterSelectionTransform;
}

function cloneTransform(transform: Readonly<RasterSelectionTransform>): RasterSelectionTransform {
  return {
    ...transform,
    centre: { ...transform.centre },
  };
}

function actionForHandle(
  handle: RasterSelectionHandle,
): Extract<RasterSelectionAction, "move" | "resize" | "rotate"> {
  if (handle === "rotate") return "rotate";
  if (handle === "body") return "move";
  return "resize";
}

export class SelectionInteractionController {
  private original: RasterSelectionTransform | null = null;
  private preview: RasterSelectionTransform | null = null;
  private constraint: IntegerRect | null = null;
  private startPoint: Point | null = null;
  private activationPoint: Point | null = null;
  private startZoom = 1;
  private activated = false;
  private handle: RasterSelectionHandle = "body";
  private rotationHandleSide: RasterSelectionRotationHandleSide | null = null;
  private rotationStartAngle: number | null = null;

  public constructor(
    private readonly activationThresholdCssPixels = DEFAULT_RASTER_TRANSFORM_THRESHOLD_CSS_PIXELS,
  ) {}

  public begin(
    transform: Readonly<RasterSelectionTransform>,
    constraint: Readonly<IntegerRect>,
    handle: RasterSelectionHandle,
    point: Readonly<Point>,
    viewportZoom: number,
  ): void {
    this.original = cloneTransform(transform);
    this.preview = null;
    this.constraint = { ...constraint };
    this.startPoint = { ...point };
    this.activationPoint = null;
    this.startZoom = Number.isFinite(viewportZoom) ? Math.max(0.05, viewportZoom) : 1;
    this.activated = false;
    this.handle = handle;
    if (handle === "rotate") {
      this.rotationHandleSide = selectionRotationHandlePlacement(
        transform,
        this.startZoom,
        constraint,
      ).side;
      this.rotationStartAngle = selectionPointerAngleDegrees(transform.centre, point);
    } else {
      this.rotationHandleSide = null;
      this.rotationStartAngle = null;
    }
  }

  public update(point: Readonly<Point>): RasterSelectionTransform | null {
    if (!this.original || !this.constraint || !this.startPoint) return null;
    const travel = { x: point.x - this.startPoint.x, y: point.y - this.startPoint.y };
    const documentDistance = Math.hypot(travel.x, travel.y);
    const screenDistance = documentDistance * this.startZoom;
    if (!this.activated) {
      if (screenDistance < Math.max(0, this.activationThresholdCssPixels)) return null;
      this.activated = true;
      const thresholdDocumentDistance =
        Math.max(0, this.activationThresholdCssPixels) / this.startZoom;
      const thresholdRatio =
        documentDistance > 0 ? Math.min(1, thresholdDocumentDistance / documentDistance) : 0;
      this.activationPoint = {
        x: this.startPoint.x + travel.x * thresholdRatio,
        y: this.startPoint.y + travel.y * thresholdRatio,
      };
    }
    if (!this.activationPoint) return null;
    const activeDelta = {
      x: point.x - this.activationPoint.x,
      y: point.y - this.activationPoint.y,
    };
    if (this.handle === "body") {
      this.preview = moveSelectionTransform(this.original, activeDelta, this.constraint);
    } else if (this.handle === "rotate") {
      if (this.rotationStartAngle === null) return null;
      // Keep the four-CSS-pixel activation dead zone without changing the
      // rotation reference: at activation, effectivePointer equals the actual
      // pointer-down vector, so neither handle side can jump.
      const effectivePointer = {
        x: this.startPoint.x + activeDelta.x,
        y: this.startPoint.y + activeDelta.y,
      };
      const currentAngle = selectionPointerAngleDegrees(this.original.centre, effectivePointer);
      const angularDelta = shortestSignedAngularDeltaDegrees(this.rotationStartAngle, currentAngle);
      this.preview = rotateSelectionTransform(this.original, angularDelta, this.constraint);
    } else {
      const originalHandle = selectionTransformCorners(this.original)[this.handle];
      this.preview = resizeSelectionTransform(
        this.original,
        this.handle,
        { x: originalHandle.x + activeDelta.x, y: originalHandle.y + activeDelta.y },
        this.constraint,
      );
    }
    return this.getPreview();
  }

  public finish(): CompletedRasterSelectionTransform | null {
    if (!this.original || !this.preview || !this.activated) {
      this.reset();
      return null;
    }
    const before = cloneTransform(this.original);
    const after = cloneTransform(this.preview);
    const action = actionForHandle(this.handle);
    this.reset();
    return transformsDiffer(before, after) ? { action, before, after } : null;
  }

  public cancel(): void {
    this.reset();
  }

  public getPreview(): RasterSelectionTransform | null {
    return this.preview ? cloneTransform(this.preview) : null;
  }

  public isActive(): boolean {
    return this.original !== null;
  }

  public isActivated(): boolean {
    return this.activated;
  }

  public getRotationHandleSide(): RasterSelectionRotationHandleSide | null {
    return this.rotationHandleSide;
  }

  private reset(): void {
    this.original = null;
    this.preview = null;
    this.constraint = null;
    this.startPoint = null;
    this.activationPoint = null;
    this.startZoom = 1;
    this.activated = false;
    this.handle = "body";
    this.rotationHandleSide = null;
    this.rotationStartAngle = null;
  }
}
